import React, {FC, useCallback, useEffect, useState} from 'react'
import {Badge, Button, Form, Modal, Nav, Spinner, Alert, OverlayTrigger, Tooltip} from 'react-bootstrap'
import {useAppDispatch, useAppSelector} from 'src/redux/storeHooks'
import useAppToast, { getApiErrorMsg } from 'src/hooks/useAppToast'
import {
  getOrderPackageListAsync,
  getOrderLinesForPackageAsync,
  createOrderPackageAsync,
  updateOrderPackageAsync,
  deleteOrderPackageAsync,
  getOrderPackageDetailAsync,
} from 'src/redux/actions/orderPackageActions'
import {
  getPackageListAsync,
  createPackageAsync,
  updatePackageAsync,
  deletePackageAsync,
} from 'src/redux/actions/packageActions'
import ShipmentFulfillmentForm, { ShipmentModalMode } from './ShipmentFulfillmentForm'
import CommonConfirmationModal from 'src/utils/modals/ConfirmationModal'
import PackageDimensionSelect from './PackageDimensionSelect'
import { determineShippingRoute, ShippingRoute, hasPendingQty } from './determineShippingRoute'
import { 
  WEIGHT_UNIT_OPTIONS, 
  DIMENSION_UNIT_OPTIONS, 
  normalizeWeightUnit, 
  normalizeDimensionUnit 
} from 'src/utils/constants'

// ===================================================================
// Types
// ===================================================================

type ManagePackagesModalProps = {
  show: boolean
  onClose: () => void
  orderId: number | null
  orderCode: string
  initialOpenId?: number | null
  forceViewMode?: boolean
  onSwitchRoute?: () => void
}

type TabKey = 'order-packages' | 'package-presets'

// ===================================================================
// Constants
// ===================================================================

// Use central constants for unit options
const WEIGHT_UNITS = WEIGHT_UNIT_OPTIONS
const DIMENSION_UNITS = DIMENSION_UNIT_OPTIONS

// Truck / shipment icon
const ShipTruckIcon: FC<{color?: string}> = ({color = '#555'}) => (
  <svg width='16' height='16' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path
      d='M1 3H15V16H1V3Z'
      stroke={color}
      strokeWidth='1.8'
      strokeLinejoin='round'
    />
    <path
      d='M15 8H19L23 12V16H15V8Z'
      stroke={color}
      strokeWidth='1.8'
      strokeLinejoin='round'
    />
    <circle cx='5.5' cy='18.5' r='2.5' stroke={color} strokeWidth='1.8' />
    <circle cx='18.5' cy='18.5' r='2.5' stroke={color} strokeWidth='1.8' />
  </svg>
)

// Delivery / mark-delivered icon (box with checkmark)
const DeliveryCheckIcon: FC<{color?: string}> = ({color = '#28a745'}) => (
  <svg width='16' height='16' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path d='M20 7H4C3.45 7 3 7.45 3 8V19C3 19.55 3.45 20 4 20H20C20.55 20 21 19.55 21 19V8C21 7.45 20.55 7 20 7Z' stroke={color} strokeWidth='1.8' strokeLinejoin='round' />
    <path d='M12 7V4L16 4V7' stroke={color} strokeWidth='1.8' strokeLinejoin='round' />
    <path d='M8 4H16' stroke={color} strokeWidth='1.8' strokeLinecap='round' />
    <path d='M8 4V7' stroke={color} strokeWidth='1.8' strokeLinecap='round' />
    <path d='M9 13L11 15L15 11' stroke={color} strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
  </svg>
)

// Eye / view-only icon (for delivered packages)
const EyeViewIcon: FC<{color?: string}> = ({color = '#6c757d'}) => (
  <svg width='16' height='16' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path d='M1 12C1 12 5 4 12 4C19 4 23 12 23 12C23 12 19 20 12 20C5 20 1 12 1 12Z' stroke={color} strokeWidth='1.8' strokeLinejoin='round' />
    <circle cx='12' cy='12' r='3' stroke={color} strokeWidth='1.8' />
  </svg>
)

// ===================================================================
// Helpers
// ===================================================================

const extractDimensionsFromPreset = (preset: any) => {
  const fields = preset?.additional_info?.[0]?.fields_data || []
  const getFieldVal = (code: string) => {
    const field = fields.find((f: any) => f.field_code === code)
    return field?.field_val ?? ''
  }
  return {
    packageLength: String(getFieldVal('PACKAGE_LENGTH')),
    packageWidth: String(getFieldVal('PACKAGE_WIDTH')),
    packageHeight: String(getFieldVal('PACKAGE_HEIGHT')),
    packageWeight: String(getFieldVal('PACKAGE_WEIGHT')),
    weightUnit: normalizeWeightUnit(getFieldVal('WEIGHT_UNIT')),
    dimensionUnit: normalizeDimensionUnit(getFieldVal('DIMENSION_UNIT')),
    packageName: preset?.api_name || '',
  }
}

// ===================================================================
// Component
// ===================================================================

const ManagePackagesModal: FC<ManagePackagesModalProps> = ({show, onClose, orderId, orderCode, initialOpenId, forceViewMode, onSwitchRoute}) => {
  const dispatch = useAppDispatch()
  const {successToast, warningToast, errorToast} = useAppToast()

  // Redux state
  const {orderPackageList, loading: orderPkgLoading} = useAppSelector(
    (s) => s.orderPackageSlice
  )
  const {orderLinesList, orderLinesLoading} = useAppSelector((s) => s.orderPackageSlice)
  const {packageList, loading: presetsLoading, mutationLoading: presetMutating} = useAppSelector(
    (s) => s.packageSlice
  )
  const orderPkgMutating = useAppSelector((s) => s.orderPackageSlice.mutationLoading)
  const orderPkgDetailLoading = useAppSelector((s) => s.orderPackageSlice.detailLoading)

  // Local state
  const [activeTab, setActiveTab] = useState<TabKey>('order-packages')
  const [selectedPresetId, setSelectedPresetId] = useState<number | null>(null)
  const [selectedPresetObject, setSelectedPresetObject] = useState<any | null>(null)
  const [isCreatingNewPackage, setIsCreatingNewPackage] = useState(false)
  const [initialLoading, setInitialLoading] = useState(true)
  const [shippingRoute, setShippingRoute] = useState<ShippingRoute>('open')

  // Form fields (shared between both tabs' forms)
  const [packageName, setPackageName] = useState('')
  const [packageLength, setPackageLength] = useState('')
  const [packageWidth, setPackageWidth] = useState('')
  const [packageHeight, setPackageHeight] = useState('')
  const [dimensionUnit, setDimensionUnit] = useState('CM')
  const [packageWeight, setPackageWeight] = useState('')
  const [weightUnit, setWeightUnit] = useState('G')
  const [qtyToPack, setQtyToPack] = useState<Record<number, number>>({})

  // Preset tab state
  const [presetEditId, setPresetEditId] = useState<number | null>(null)

  // Order-package edit state (null = create mode, number = editing that order-package)
  const [editingOrderPkgId, setEditingOrderPkgId] = useState<number | null>(null)
  const [editingOrderPkgIsFulfilled, setEditingOrderPkgIsFulfilled] = useState(false)
  const [editingOrderPkgShipmentStatus, setEditingOrderPkgShipmentStatus] = useState<string | null>(null)

  // ── Shipment Fulfillment Inline form states ──
  const [expandedFulfillmentPkgId, setExpandedFulfillmentPkgId] = useState<number | null>(null)
  const [expandedFulfillmentPkgIndex, setExpandedFulfillmentPkgIndex] = useState<number | null>(null)
  const [activeFulfillmentMode, setActiveFulfillmentMode] = useState<ShipmentModalMode>('create')
  const [shipmentModalLoadingId, setShipmentModalLoadingId] = useState<number | null>(null)
  const [deletePkgModalState, setDeletePkgModalState] = useState<{ show: boolean, id: number | null }>({ show: false, id: null })

  // ---------------------------------------------------------------
  // Data fetching
  // ---------------------------------------------------------------

  const fetchOrderPackages = useCallback(() => {
    if (!orderId) return
    dispatch(getOrderPackageListAsync({page: 1, limit: 50, order_id: orderId}))
  }, [dispatch, orderId])

  const fetchOrderLines = useCallback(() => {
    if (!orderId) return
    dispatch(getOrderLinesForPackageAsync({orderId, page: 1, limit: 50, direct_shipment: false}))
  }, [dispatch, orderId])

  const fetchPresets = useCallback(() => {
    dispatch(getPackageListAsync({page: 1, limit: 100}))
  }, [dispatch])

  // ── Synchronous state reset on modal open (prevents stale-frame flicker) ──
  const [prevShow, setPrevShow] = useState(false)
  if (show && !prevShow) {
    setPrevShow(true)
    setInitialLoading(true)
    setShippingRoute('open')
    setActiveTab('order-packages')
    setPresetEditId(null)
    setIsCreatingNewPackage(false)
    setEditingOrderPkgId(null)
  }
  if (!show && prevShow) {
    setPrevShow(false)
  }

  const fetchInitialData = async () => {
    if (!orderId) return
    setInitialLoading(true)
    try {
      const route = await determineShippingRoute(orderId, dispatch)
      setShippingRoute(route)

      // Fetch all required data in parallel
      await Promise.all([
        dispatch(getOrderPackageListAsync({page: 1, limit: 50, order_id: orderId})).unwrap(),
        dispatch(getOrderLinesForPackageAsync({orderId, page: 1, limit: 50, direct_shipment: false})).unwrap(),
        dispatch(getPackageListAsync({page: 1, limit: 100})).unwrap()
      ])
    } catch (err: any) {
      errorToast(getApiErrorMsg(err, 'Failed to fetch package data'))
    } finally {
      setInitialLoading(false)
    }
  }

  useEffect(() => {
    if (show && orderId) {
      fetchInitialData()
    }
    // eslint-disable-line react-hooks/exhaustive-deps
  }, [show, orderId])

  useEffect(() => {
    if (show && initialOpenId && !initialLoading) {
      handleEditOrderPackage(initialOpenId)
    }
    // eslint-disable-line react-hooks/exhaustive-deps
  }, [show, initialOpenId, initialLoading])

  // Reset form when modal closes
  useEffect(() => {
    if (!show) {
      resetForm()
      setActiveTab('order-packages')
      setPresetEditId(null)
    }
  }, [show])

  // ---------------------------------------------------------------
  // Form helpers
  // ---------------------------------------------------------------

  const resetForm = () => {
    setSelectedPresetId(null)
    setSelectedPresetObject(null)
    setPackageName('')
    setPackageLength('')
    setPackageWidth('')
    setPackageHeight('')
    setDimensionUnit('CM')
    setPackageWeight('')
    setWeightUnit('G')
    setQtyToPack({})
    setEditingOrderPkgId(null)
    setEditingOrderPkgIsFulfilled(false)
    setEditingOrderPkgShipmentStatus(null)
    setIsCreatingNewPackage(false)
  }

  const handlePresetSelect = (val: any | '__new__') => {
    if (val === '__new__') {
      setSelectedPresetId(null)
      setSelectedPresetObject('__new__')
      // Clear dimensions and package name when selecting New Dimension
      setPackageName('')
      setPackageLength('')
      setPackageWidth('')
      setPackageHeight('')
      setDimensionUnit('CM')
      return
    }
    const preset = val
    const id = preset.additional_info?.[0]?.platformfielddata ?? preset.id
    setSelectedPresetId(id)
    setSelectedPresetObject(preset)

    const dims = extractDimensionsFromPreset(preset)
    // Hydrate dimensions AND package name
    setPackageName(dims.packageName)
    setPackageLength(dims.packageLength)
    setPackageWidth(dims.packageWidth)
    setPackageHeight(dims.packageHeight)
    setDimensionUnit(dims.dimensionUnit)
  }

  const handleQtyChange = (itemId: number, value: string, pendingQty: number) => {
    const num = Math.max(0, Math.min(Number(value) || 0, pendingQty))
    setQtyToPack((prev) => ({...prev, [itemId]: num}))
  }

  // ---------------------------------------------------------------
  // Preset label helper
  // ---------------------------------------------------------------

  const presetLabel = useCallback(
    (preset: any) => {
      const dims = extractDimensionsFromPreset(preset)
      return `${preset.api_name} (${dims.packageLength} x ${dims.packageWidth} x ${dims.packageHeight})`
    },
    []
  )

  // ---------------------------------------------------------------
  // Submit: Create Order Package
  // ---------------------------------------------------------------

  const handleCreateOrderPackage = async () => {
    if (!orderId) return

    // Validate dimensions
    if (!packageLength || !packageWidth || !packageHeight) {
      warningToast('Please fill in all dimension fields (L, W, H).')
      return
    }

    // Validate at least one item
    const itemsToSubmit = Object.entries(qtyToPack).filter(([_, qty]) => qty > 0)
    if (itemsToSubmit.length === 0) {
      warningToast('Please set Qty to Pack for at least one item.')
      return
    }

    // Validate qty doesn't exceed pending
    for (const [itemIdStr, qty] of itemsToSubmit) {
      const line = orderLinesList.find((l: any) => l.id === Number(itemIdStr))
      if (line && qty > line.pending_qty) {
        warningToast(`Qty to Pack for "${line.name}" exceeds pending quantity.`)
        return
      }
    }

    const payload = {
      order_id: orderId,
      order_items: itemsToSubmit.map(([itemId, qty]) => ({
        order_item_id: Number(itemId),
        package_quantity: qty,
      })),
      package_name: packageName || null,
      package_dimension: selectedPresetId || null,
      package_length: Number(packageLength),
      package_width: Number(packageWidth),
      package_height: Number(packageHeight),
      // package_weight: Number(packageWeight), // hide this for now
      weight_unit: weightUnit,
      dimension_unit: dimensionUnit,
      weight: Number(packageWeight),
      // order_package_weight: Number(packageWeight), // hide this for now
      // order_package_weight_unit: weightUnit, // hide this for now
    }

    try {
      const res = await dispatch(createOrderPackageAsync(payload)).unwrap()
      if (res?.data || res?.id) {
        successToast('Order package created successfully.')
        resetForm()
        fetchOrderPackages()
        fetchOrderLines()
      }
    } catch (err: any) {
      errorToast(getApiErrorMsg(err, 'Failed to create order package.'))
    }
  }

  // ---------------------------------------------------------------
  // Submit: Update Order Package
  // ---------------------------------------------------------------

  const handleUpdateOrderPackage = async () => {
    if (!orderId || !editingOrderPkgId) return

    // Validate dimensions
    if (!packageLength || !packageWidth || !packageHeight) {
      warningToast('Please fill in all dimension fields (L, W, H).')
      return
    }

    // Validate at least one item
    const itemsToSubmit = Object.entries(qtyToPack).filter(([_, qty]) => qty > 0)
    if (itemsToSubmit.length === 0) {
      warningToast('Please set Qty to Pack for at least one item.')
      return
    }

    const payload = {
      order_id: orderId,
      order_items: itemsToSubmit.map(([itemId, qty]) => ({
        order_item_id: Number(itemId),
        package_quantity: qty,
      })),
      package_name: packageName || null,
      package_dimension: selectedPresetId || null,
      package_length: Number(packageLength),
      package_width: Number(packageWidth),
      package_height: Number(packageHeight),
      // package_weight: Number(packageWeight), // hide this for now
      weight_unit: weightUnit,
      dimension_unit: dimensionUnit,
      weight: Number(packageWeight),
      // order_package_weight: Number(packageWeight), // hide this for now
      // order_package_weight_unit: weightUnit, // hide this for now
    }

    try {
      await dispatch(
        updateOrderPackageAsync({id: editingOrderPkgId, payloadupdate: payload})
      ).unwrap()
      successToast('Order package updated successfully.')
      resetForm()
      fetchOrderPackages()
      fetchOrderLines()
    } catch (err: any) {
      errorToast(getApiErrorMsg(err, 'Failed to update order package.'))
    }
  }

  // ---------------------------------------------------------------
  // Delete Order Package
  // ---------------------------------------------------------------

  const handleDeleteOrderPackage = (pkgId: number) => {
    setDeletePkgModalState({ show: true, id: pkgId })
  }

  const proceedDeleteOrderPackage = async () => {
    if (!deletePkgModalState.id) return
    const pkgId = deletePkgModalState.id
    try {
      await dispatch(deleteOrderPackageAsync({id: pkgId})).unwrap()
      successToast('Order package deleted successfully.')
      if (editingOrderPkgId === pkgId) {
        resetForm()
      }
      fetchOrderPackages()
      fetchOrderLines()
    } catch (err: any) {
      errorToast(getApiErrorMsg(err, 'Failed to delete order package.'))
    } finally {
      setDeletePkgModalState({ show: false, id: null })
    }
  }

  // ---------------------------------------------------------------
  // Edit Order Package — fetch fresh detail from API, then hydrate form
  // ---------------------------------------------------------------

  const handleEditOrderPackage = async (pkgId: number) => {
    try {
      const res = await dispatch(getOrderPackageDetailAsync(pkgId)).unwrap()
      // API returns { data: [ {...} ] }
      const pkg = Array.isArray(res?.data) ? res.data[0] : res?.data
      if (!pkg) {
        if (!forceViewMode) {
          errorToast('Could not load package details. Please try again.')
        }
        return
      }

      // ── Dimensions from package_dimension_data ──
      // New format: full nested preset object with additional_info[].fields_data[]
      // Legacy format: flat object { PACKAGE_LENGTH, PACKAGE_WIDTH, ... }
      const dimData = pkg.package_dimension_data ?? {}
      const isNestedFormat = Array.isArray(dimData.additional_info) && dimData.additional_info.length > 0

      let pkgLength = ''
      let pkgWidth = ''
      let pkgHeight = ''
      let pkgWeight = ''
      let dimUnit = 'CM'
      let dimWtUnit = 'G'

      if (isNestedFormat) {
        // New nested format — reuse extractDimensionsFromPreset which reads additional_info[0].fields_data
        const extracted = extractDimensionsFromPreset(dimData)
        pkgLength = extracted.packageLength
        pkgWidth  = extracted.packageWidth
        pkgHeight = extracted.packageHeight
        pkgWeight = extracted.packageWeight
        dimUnit   = normalizeDimensionUnit(extracted.dimensionUnit)
        dimWtUnit = normalizeWeightUnit(extracted.weightUnit)
      } else {
        // Legacy flat format
        pkgLength = dimData.PACKAGE_LENGTH  != null ? String(dimData.PACKAGE_LENGTH)  : ''
        pkgWidth  = dimData.PACKAGE_WIDTH   != null ? String(dimData.PACKAGE_WIDTH)   : ''
        pkgHeight = dimData.PACKAGE_HEIGHT  != null ? String(dimData.PACKAGE_HEIGHT)  : ''
        pkgWeight = dimData.PACKAGE_WEIGHT  != null ? String(dimData.PACKAGE_WEIGHT)  : ''
        dimUnit   = normalizeDimensionUnit(dimData.DIMENSION_UNIT)
        dimWtUnit = normalizeWeightUnit(dimData.WEIGHT_UNIT)
      }

      // ── Order-package weight (the actual parcel weight) ──
      // BE returns `weight` and `weight_unit` at the top level
      const opWeight    = pkg.weight     != null ? String(pkg.weight)    : pkgWeight
      const opWeightUnit= normalizeWeightUnit(pkg.weight_unit ?? (isNestedFormat ? undefined : dimData.WEIGHT_UNIT))

      // ── Package name ──
      const pkgName = pkg.package_dimension_data?.package_name || pkg.package_name || pkg.name || ''

      // ── Preset dimension ID (for hydrating the "Select Package Size" dropdown) ──
      // Order packages API nested format: dimData.field_data_id matches the preset ID, 
      // with fallback to additional_info / package_dimension
      const presetId = dimData.field_data_id ?? dimData.id ?? dimData.additional_info?.[0]?.platformfielddata ?? pkg.package_dimension ?? null

      // Synthesize a fallback preset object so the dropdown can display a label
      // even if the preset isn't in the initial page of options.
      let hydratedPresetObject: any = null
      if (presetId) {
        hydratedPresetObject = {
          id: presetId,
          api_name: dimData.package_name || pkgName || `Preset ${presetId}`,
          isHydratedFallback: true,
          packageLength: pkgLength,
          packageWidth: pkgWidth,
          packageHeight: pkgHeight,
        }
      }

      // ── Line items → qtyToPack map ──
      // Each line_item has: { id (line-item DB id), platform_order_line_item, package_quantity }
      // We must key by platform_order_line_item so it matches orderLinesList item IDs.
      const qtyMap: Record<number, number> = {}
      if (Array.isArray(pkg.line_items)) {
        pkg.line_items.forEach((li: any) => {
          // platform_order_line_item is the FK that matches orderLinesList[].id
          const lineItemId = li.platform_order_line_item ?? li.order_item_id ?? li.id
          if (lineItemId != null) {
            qtyMap[lineItemId] = li.package_quantity ?? 0
          }
        })
      }

      // ── Hydrate form state ──
      setEditingOrderPkgId(pkgId)
      setPackageName(pkgName)
      setPackageLength(pkgLength)
      setPackageWidth(pkgWidth)
      setPackageHeight(pkgHeight)
      setDimensionUnit(dimUnit)
      setPackageWeight(opWeight)
      setWeightUnit(opWeightUnit)
      setSelectedPresetId(presetId)
      setSelectedPresetObject(hydratedPresetObject)
      const pkgShipmentStatus = pkg.shipment?.shipment_status ?? null
      const isFulfilledOrShipped = pkgShipmentStatus === 'SHIPPED' || pkgShipmentStatus === 'DELIVERED'
      setEditingOrderPkgIsFulfilled(isFulfilledOrShipped)
      setEditingOrderPkgShipmentStatus(pkgShipmentStatus)
      setQtyToPack(qtyMap)
    } catch (err: any) {
      if (!forceViewMode) {
        errorToast(getApiErrorMsg(err, 'Failed to load package details.'))
      }
    }
  }

  const handleCancelOrderPkgEdit = () => {
    resetForm()
  }

  // ---------------------------------------------------------------
  // Shipment Fulfillment Inline handlers
  // ---------------------------------------------------------------

  const handleOpenInlineShipmentForm = (pkg: any, idx: number) => {
    // Close other forms
    setPresetEditId(null)
    setEditingOrderPkgId(null)
    
    const status = pkg.shipment?.shipment_status
    const mode = status === 'DELIVERED' ? 'view' : status === 'SHIPPED' ? 'deliver' : 'create'

    setActiveFulfillmentMode(mode)
    setExpandedFulfillmentPkgId(pkg.id)
    setExpandedFulfillmentPkgIndex(idx)
  }

  const handleShipmentSuccess = () => {
    fetchOrderPackages()
  }

  // ---------------------------------------------------------------
  // Submit: Create / Update / Delete Preset
  // ---------------------------------------------------------------

  const buildPresetPayload = () => ({
    package_name: packageName,
    package_length: Number(packageLength),
    package_width: Number(packageWidth),
    package_height: Number(packageHeight),
    package_weight: Number(packageWeight),
    weight_unit: weightUnit,
    dimension_unit: dimensionUnit,
  })

  const handleSavePreset = async () => {
    if (!packageName.trim()) {
      warningToast('Please enter a package name.')
      return
    }
    if (!packageLength || !packageWidth || !packageHeight) {
      warningToast('Please fill in all dimension fields (L, W, H).')
      return
    }

    try {
      if (presetEditId) {
        await dispatch(
          updatePackageAsync({id: presetEditId, payloadupdate: buildPresetPayload()})
        ).unwrap()
        successToast('Package preset updated successfully.')
      } else {
        await dispatch(createPackageAsync(buildPresetPayload())).unwrap()
        successToast('Package preset created successfully.')
      }
      resetForm()
      setPresetEditId(null)
      fetchPresets()
    } catch (err: any) {
      errorToast(getApiErrorMsg(err, 'Failed to save package preset.'))
    }
  }

  const handleEditPreset = (preset: any) => {
    setPresetEditId(preset.id)
    const dims = extractDimensionsFromPreset(preset)
    setPackageName(dims.packageName)
    setPackageLength(dims.packageLength)
    setPackageWidth(dims.packageWidth)
    setPackageHeight(dims.packageHeight)
    setDimensionUnit(dims.dimensionUnit)
    setPackageWeight(dims.packageWeight)
    setWeightUnit(dims.weightUnit)
  }

  const handleDeletePreset = async (id: number) => {
    try {
      await dispatch(deletePackageAsync({id})).unwrap()
      successToast('Package preset deleted.')
      if (presetEditId === id) {
        resetForm()
        setPresetEditId(null)
      }
      fetchPresets()
    } catch (err: any) {
      errorToast(getApiErrorMsg(err, 'Failed to delete package preset.'))
    }
  }

  const handleCancelPresetEdit = () => {
    resetForm()
    setPresetEditId(null)
  }

  // ---------------------------------------------------------------
  // Computed
  // ---------------------------------------------------------------

  const saving = orderPkgMutating || presetMutating
  const editLoading = orderPkgDetailLoading
  const loading = orderPkgLoading || orderLinesLoading || presetsLoading

  // ---------------------------------------------------------------
  // Sub-renders
  // ---------------------------------------------------------------

  const renderPackageItems = (pkg: any) => {
    if (!pkg.line_items || pkg.line_items.length === 0) {
      return <span className='text-muted small'>No items in this package.</span>
    }
    return (
      <div className='d-flex flex-column gap-1'>
        {pkg.line_items.map((li: any) => {
          const orderLineId = li.platform_order_line_item ?? li.order_item_id ?? li.id
          const matchedItem = orderLinesList.find((item: any) => item.id === orderLineId)
          const name = li.name ?? matchedItem?.name ?? matchedItem?.platform_product?.name ?? `Item #${orderLineId}`
          const qty = li.package_quantity ?? 0
          return (
            <small key={li.id || orderLineId} className='text-muted' style={{fontSize: '13px', display: 'block'}}>
              {qty} x {name}
            </small>
          )
        })}
      </div>
    )
  }

  const renderPackageForm = (isEdit: boolean, packageIndex?: number) => {
    const disabledForm = forceViewMode || (isEdit && (editingOrderPkgIsFulfilled || !!editingOrderPkgShipmentStatus))

    return (
      <div
        className='border rounded mb-3'
        style={{backgroundColor: '#fff'}}
      >
        {/* Card header - package title + Edit/Delete buttons */}
        <div
          className='d-flex justify-content-between align-items-center px-4 py-3'
          style={{borderBottom: '1px solid #dee2e6', backgroundColor: '#f8f9fa'}}
        >
          <div className='d-flex align-items-center gap-2' style={{flex: 1}}>
            <span style={{fontSize: '18px'}}>{"\uD83D\uDCE6"}</span>
            <span className='fw-bold' style={{fontSize: '15px', whiteSpace: 'nowrap'}}>
              {isEdit ? `Package ${packageIndex !== undefined ? packageIndex + 1 : `#${editingOrderPkgId}`}` : 'Create Package'}
            </span>
            {isEdit && (
              <Badge 
                bg={
                  editingOrderPkgShipmentStatus === 'DELIVERED' ? 'success' :
                  editingOrderPkgShipmentStatus === 'SHIPPED' ? 'primary' :
                  'warning text-dark'
                } 
                className='ms-2'
                style={{ fontSize: '11px' }}
              >
                {editingOrderPkgShipmentStatus || 'PENDING'}
              </Badge>
            )}
          </div>
          <div className='d-flex gap-2'>
            {isEdit ? (
              <>
                <Button
                  variant='light'
                  size='sm'
                  style={{fontSize: '13px', padding: '6px 16px', fontWeight: 500, color: '#555', border: '1px solid #dee2e6', borderRadius: '6px'}}
                  onClick={handleCancelOrderPkgEdit}
                >
                  Cancel
                </Button>
                {!disabledForm && (
                  <Button
                    variant='outline-danger'
                    size='sm'
                    style={{fontSize: '13px', padding: '4px 14px'}}
                    onClick={() => handleDeleteOrderPackage(editingOrderPkgId!)}
                    disabled={orderPkgMutating}
                  >
                    Delete
                  </Button>
                )}
              </>
            ) : (
              <Button
                variant='light'
                size='sm'
                style={{fontSize: '13px', padding: '6px 16px', fontWeight: 500, color: '#555', border: '1px solid #dee2e6', borderRadius: '6px'}}
                onClick={() => {
                  resetForm()
                  setIsCreatingNewPackage(false)
                }}
              >
                Cancel
              </Button>
            )}
          </div>
        </div>

        {/* Form body */}
        <div className='px-4 py-3'>
          {/* Row 1: Select Package Size (left) + Order Package Weight (right) */}
          <div className='d-flex gap-4 mb-3' style={{flexWrap: 'wrap'}}>
            <div style={{flex: '1 1 55%', minWidth: '280px'}}>
              <label className='form-label fw-bold mb-1' style={{fontSize: '13px', color: '#333'}}>
                Select Package Size
              </label>
              <PackageDimensionSelect
                value={selectedPresetObject}
                onChange={handlePresetSelect}
                disabled={disabledForm}
                extractDimensionsFromPreset={extractDimensionsFromPreset}
              />
            </div>
            <div style={{flex: '0 1 35%', minWidth: '200px'}}>
              <label className='form-label fw-bold mb-1' style={{fontSize: '13px', color: '#333'}}>
                Weight
                {isEdit && (
                  <span className='text-muted fw-normal ms-1' style={{fontSize: '12px'}}>
                    (actual parcel weight)
                  </span>
                )}
              </label>
              <div className='d-flex align-items-center gap-2'>
                <Form.Control
                  type='number'
                  min='0'
                  step='0.01'
                  placeholder='0.00'
                  value={packageWeight}
                  onChange={(e) => setPackageWeight(e.target.value)}
                  style={{fontSize: '14px'}}
                  disabled={disabledForm}
                />
                <Form.Select
                  value={weightUnit}
                  style={{maxWidth: '75px', fontSize: '14px'}}
                  onChange={(e) => setWeightUnit(e.target.value)}
                  disabled={disabledForm}
                >
                  {WEIGHT_UNITS.map((u) => (
                    <option key={u.value} value={u.value}>
                      {u.label}
                    </option>
                  ))}
                </Form.Select>
              </div>
            </div>
          </div>

          {/* Row 2: L x W x H on gray background strip + Package Name (Optional) */}
          <div className='d-flex align-items-center gap-4 mb-3' style={{flexWrap: 'wrap'}}>
            <div style={{flex: '0 0 auto'}}>
              <label className='form-label fw-bold mb-1' style={{fontSize: '13px', color: '#333'}}>
                Dimensions (L x W x H)
              </label>
              <div
                className='d-flex align-items-center gap-2 p-2 rounded'
                style={{backgroundColor: '#f0f0f0', maxWidth: '460px'}}
              >
                <Form.Control
                  type='number'
                  min='0'
                  step='0.01'
                  placeholder='L'
                  value={packageLength}
                  onChange={(e) => setPackageLength(e.target.value)}
                  style={{maxWidth: '80px', fontSize: '14px'}}
                  disabled={disabledForm || selectedPresetId !== null}
                />
                <span className='fw-bold text-muted'>x</span>
                <Form.Control
                  type='number'
                  min='0'
                  step='0.01'
                  placeholder='W'
                  value={packageWidth}
                  onChange={(e) => setPackageWidth(e.target.value)}
                  style={{maxWidth: '80px', fontSize: '14px'}}
                  disabled={disabledForm || selectedPresetId !== null}
                />
                <span className='fw-bold text-muted'>x</span>
                <Form.Control
                  type='number'
                  min='0'
                  step='0.01'
                  placeholder='H'
                  value={packageHeight}
                  onChange={(e) => setPackageHeight(e.target.value)}
                  style={{maxWidth: '80px', fontSize: '14px'}}
                  disabled={disabledForm || selectedPresetId !== null}
                />
                <Form.Select
                  value={dimensionUnit}
                  style={{maxWidth: '75px', fontSize: '14px'}}
                  onChange={(e) => setDimensionUnit(e.target.value)}
                  disabled={disabledForm || selectedPresetId !== null}
                >
                  {DIMENSION_UNITS.map((u) => (
                    <option key={u.value} value={u.value}>
                      {u.label}
                    </option>
                  ))}
                </Form.Select>
              </div>
            </div>

            <div style={{flex: '1 1 200px', minWidth: '180px'}}>
              <label className='form-label fw-bold mb-1' style={{fontSize: '13px', color: '#333'}}>
                Package Name (Optional)
              </label>
              <Form.Control
                type='text'
                value={packageName}
                onChange={(e) => setPackageName(e.target.value)}
                disabled={disabledForm}
                placeholder='Package Name'
                style={{fontSize: '14px'}}
              />
            </div>
          </div>



          {/* Items to Pack */}
          <div className='mt-3'>
            <h6 className='fw-bold mb-2' style={{fontSize: '14px', color: '#333'}}>Items to Pack</h6>
            {orderLinesLoading ? (
              <div className='d-flex justify-content-center py-4'>
                <Spinner animation='border' size='sm' />
              </div>
            ) : (
              <table className='table table-sm align-middle mb-0' style={{fontSize: '13px'}}>
                <thead>
                  <tr style={{borderBottom: '2px solid #dee2e6'}}>
                    <th style={{fontWeight: 600, color: '#666', padding: '8px 12px'}}>Product</th>
                    <th style={{fontWeight: 600, color: '#666', padding: '8px 12px'}}>SKU</th>
                    <th style={{fontWeight: 600, color: '#666', padding: '8px 12px'}} className='text-center'>Quantity</th>
                    <th style={{fontWeight: 600, color: '#666', padding: '8px 12px'}} className='text-center'>Packed</th>
                    <th style={{fontWeight: 600, color: '#666', padding: '8px 12px'}} className='text-center'>Pending Qty</th>
                    <th style={{fontWeight: 600, color: '#666', padding: '8px 12px', minWidth: '100px'}} className='text-center'>Qty to Pack</th>
                  </tr>
                </thead>
                <tbody>
                  {orderLinesList.length ? (
                    orderLinesList.map((item: any, idx: number) => {
                      const packed = (item.quantity ?? 0) - (item.pending_qty ?? 0)
                      
                      let alreadyInThisPackage = 0
                      if (isEdit && editingOrderPkgId) {
                        const pkgObj = orderPackageList.find((p: any) => p.id === editingOrderPkgId)
                        if (pkgObj && Array.isArray(pkgObj.line_items)) {
                          const matching = pkgObj.line_items.find((si: any) => (si.platform_order_line_item ?? si.order_item_id ?? si.id) === item.id)
                          if (matching) {
                            alreadyInThisPackage = matching.package_quantity ?? matching.quantity ?? 0
                          }
                        }
                      }
                      
                      const calculatedMax = (item.pending_qty ?? 0) + alreadyInThisPackage
                      const maxAllowed = Math.min(calculatedMax, item.quantity ?? calculatedMax)
                      
                      return (
                        <tr key={`${item.id || 'line'}-${idx}`}>
                          <td style={{padding: '8px 12px'}}>{item.name || item.title || 'N/A'}</td>
                          <td style={{padding: '8px 12px'}}>
                            {item.sku || item.platform_product_variant?.sku || 'N/A'}
                          </td>
                          <td className='text-center' style={{padding: '8px 12px'}}>{item.quantity ?? 0}</td>
                          <td className='text-center' style={{padding: '8px 12px'}}>{packed}</td>
                          <td className='text-center' style={{padding: '8px 12px'}}>
                            <span className={item.pending_qty > 0 ? 'text-success fw-semibold' : 'text-muted'}>
                              {item.pending_qty ?? 0}
                            </span>
                          </td>
                          <td className='text-center' style={{padding: '8px 12px'}}>
                            <Form.Control
                              type='number'
                              min='0'
                              max={maxAllowed}
                              value={qtyToPack[item.id] ?? 0}
                              style={{
                                maxWidth: '70px',
                                margin: '0 auto',
                                fontSize: '13px',
                                border: '1px solid #dee2e6',
                                textAlign: 'center',
                              }}
                              onChange={(e) =>
                                handleQtyChange(item.id, e.target.value, maxAllowed)
                              }
                              disabled={maxAllowed === 0 || disabledForm}
                            />
                          </td>
                        </tr>
                      )
                    })
                  ) : (
                    <tr>
                      <td colSpan={6} className='text-center text-muted py-4'>
                        No line items available.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>

          {/* Create / Update button */}
          <div className='d-flex justify-content-end mt-4'>
            {!disabledForm && (
              <Button
                variant='primary'
                onClick={isEdit ? handleUpdateOrderPackage : handleCreateOrderPackage}
                disabled={saving}
                style={{fontSize: '14px', padding: '8px 20px'}}
              >
                {orderPkgMutating ? (
                  <>
                    <Spinner animation='border' size='sm' className='me-2' />
                    {isEdit ? 'Updating...' : 'Creating...'}
                  </>
                ) : isEdit ? (
                  'Update Order Package'
                ) : (
                  'Create Order Package'
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    )
  }

  /** Tab 1 -  Order Packages */
  const renderOrderPackagesTab = () => {
    if (isCreatingNewPackage) {
      return renderPackageForm(false)
    }

    const routeBlocked = shippingRoute === 'direct'
    const noPendingQty = !initialLoading && !hasPendingQty(orderLinesList)
    const isCreateBtnDisabled = (routeBlocked || noPendingQty) && !isCreatingNewPackage

    return (
      <>
        {shippingRoute === 'direct' && (
          <Alert variant="info" className="mb-4" style={{ fontSize: '13px', border: '1px solid #b6d4fe' }}>
            ℹ️ <strong>Note:</strong> Direct shipments already exist for this order. Please use the <strong className="route-switch-link" onClick={onSwitchRoute}>'Manage Shipments'</strong> menu instead.
          </Alert>
        )}
        {/* Existing Packages */}
        <div className='mb-4'>
          <div className='d-flex justify-content-between align-items-center mb-3'>
            <h6 className='fw-bold mb-0' style={{fontSize: '15px', color: '#333'}}>Existing Packages</h6>
            <OverlayTrigger
              placement="left"
              overlay={
                isCreateBtnDisabled ? (
                  <Tooltip id="has-shipments-tooltip">
                    {routeBlocked 
                      ? "Direct shipments already exist for this order. Please use the 'Manage Shipments' menu."
                      : "All items have been fulfilled."}
                  </Tooltip>
                ) : <Tooltip id="new-package-tooltip">Create a new package for this order</Tooltip>
              }
            >
              <span className="d-inline-block">
                <Button
                  variant={isCreatingNewPackage ? 'light' : 'primary'}
                  size='sm'
                  disabled={isCreateBtnDisabled}
                  style={{
                    fontSize: '13px', 
                    padding: '8px 20px', 
                    fontWeight: 600,
                    borderRadius: '6px',
                    transition: 'all 0.2s ease-in-out',
                    backgroundColor: isCreatingNewPackage ? '#f1f5f9' : (isCreateBtnDisabled ? '#e2e8f0' : '#0d6efd'),
                    color: isCreatingNewPackage ? '#475569' : (isCreateBtnDisabled ? '#94a3b8' : '#fff'),
                    border: isCreatingNewPackage ? '1px solid #e2e8f0' : 'none',
                    boxShadow: isCreatingNewPackage || isCreateBtnDisabled ? 'none' : '0 4px 6px -1px rgba(13, 110, 253, 0.2)',
                    pointerEvents: isCreateBtnDisabled ? 'none' : 'auto'
                  }}
                  onClick={() => {
                    resetForm()
                    setIsCreatingNewPackage(true)
                  }}
                >
                  + Add New Package
                </Button>
              </span>
            </OverlayTrigger>
          </div>

          {orderPkgLoading ? (
            <div className='d-flex justify-content-center py-4'>
              <Spinner animation='border' size='sm' />
            </div>
          ) : orderPackageList.length === 0 ? (
            <p className='text-muted small mb-0'>No packages created yet for this order.</p>
          ) : (
            <div className='d-flex flex-column gap-3'>
              {orderPackageList.map((pkg: any, idx: number) => {
                // If this package is currently being edited, show edit form inline
                if (editingOrderPkgId === pkg.id) {
                  return <div key={pkg.id}>{renderPackageForm(true, idx)}</div>
                }

                const dimData = pkg.package_dimension_data ?? {}
                const pkgLength = dimData.PACKAGE_LENGTH ?? pkg.package_length
                const pkgWidth = dimData.PACKAGE_WIDTH ?? pkg.package_width
                const pkgHeight = dimData.PACKAGE_HEIGHT ?? pkg.package_height
                const dimUnit = normalizeDimensionUnit(dimData.DIMENSION_UNIT ?? pkg.dimension_unit)
                const dimsString = pkgLength != null && pkgWidth != null && pkgHeight != null
                  ? `${pkgLength} x ${pkgWidth} x ${pkgHeight} ${dimUnit}`
                  : 'N/A'

                const opWeight = pkg.weight
                const opWeightUnit = normalizeWeightUnit(pkg.weight_unit || 'kg')
                const weightString = opWeight != null
                  ? `${parseFloat(opWeight)} ${opWeightUnit}`
                  : 'N.A.'

                return (
                  <div
                    key={pkg.id}
                    className={`rounded border transition-all duration-200 ${expandedFulfillmentPkgId === pkg.id ? 'shadow-sm border-primary' : 'hover:shadow-md'}`}
                    style={{
                      overflow: 'hidden',
                      backgroundColor: '#fff',
                      borderColor: expandedFulfillmentPkgId === pkg.id ? '#0d6efd' : '#dee2e6',
                    }}
                  >
                    {/* Card Header */}
                    <div
                      className='px-3 py-2 d-flex justify-content-between align-items-center'
                      style={{
                        backgroundColor: '#f8f9fa',
                        borderBottom: '1px solid #dee2e6',
                      }}
                    >
                      <div className='d-flex align-items-center gap-2'>
                        <strong style={{fontSize: '14px', color: '#333'}}>
                          📦 Package {idx + 1}
                          {(pkg.package_name || pkg.name) ? (
                            <>
                              {' - '}
                              <span style={{color: '#0d6efd'}}>{pkg.package_name || pkg.name}</span>
                            </>
                          ) : null}
                        </strong>
                        {(() => {
                          const status = pkg.shipment?.shipment_status
                          if (status === 'DELIVERED') return <Badge bg='info' style={{fontSize: '10px'}}>Delivered</Badge>
                          if (status === 'SHIPPED') return <Badge bg='success' style={{fontSize: '10px'}}>Shipped</Badge>
                          return <Badge bg='warning' text='dark' style={{fontSize: '10px'}}>Pending</Badge>
                        })()}
                      </div>
                      <div className='d-flex gap-2'>
                        {!(pkg.shipment?.shipment_status === 'SHIPPED' || pkg.shipment?.shipment_status === 'DELIVERED') && !forceViewMode && (
                          <>
                            <OverlayTrigger placement="top" overlay={<Tooltip id={`edit-pkg-tooltip-${pkg.id}`}>Edit package dimensions and weight</Tooltip>}>
                              <Button
                                variant='light'
                                size='sm'
                                style={{
                                  fontSize: '13px',
                                  padding: '4px 12px',
                                  backgroundColor: '#fff',
                                  border: '1px solid #dee2e6',
                                  color: '#555',
                                }}
                                disabled={editLoading}
                                onClick={() => handleEditOrderPackage(pkg.id)}
                              >
                                {editLoading && editingOrderPkgId === pkg.id ? (
                                  <Spinner animation='border' size='sm' className='me-1' style={{width: '12px', height: '12px'}} />
                                ) : null}
                                Edit
                              </Button>
                            </OverlayTrigger>
                            <OverlayTrigger placement="top" overlay={<Tooltip id={`delete-pkg-tooltip-${pkg.id}`}>Remove this package</Tooltip>}>
                              <Button
                                variant='light'
                                size='sm'
                                style={{
                                  fontSize: '13px',
                                  padding: '4px 12px',
                                  backgroundColor: '#fff',
                                  border: '1px solid #dee2e6',
                                  color: 'red',
                                }}
                                onClick={() => handleDeleteOrderPackage(pkg.id)}
                              >
                                Delete
                              </Button>
                            </OverlayTrigger>
                          </>
                        )}
                        {/* Shipment / Delivery / View icon button */}
                        <OverlayTrigger placement="top" overlay={<Tooltip id={`ship-pkg-tooltip-${pkg.id}`}>{
                          pkg.shipment?.shipment_status === 'DELIVERED'
                            ? 'View shipment details'
                            : pkg.shipment?.shipment_status === 'SHIPPED'
                              ? 'Mark package as delivered'
                              : 'Create shipment for package'
                        }</Tooltip>}>
                          <Button
                            variant='light'
                            size='sm'

                            style={{
                              fontSize: '13px',
                              padding: '4px 8px',
                              backgroundColor: '#fff',
                              border: '1px solid #dee2e6',
                              color: pkg.shipment?.shipment_status === 'DELIVERED'
                                ? '#6c757d'
                                : pkg.shipment?.shipment_status === 'SHIPPED'
                                  ? '#28a745'
                                  : '#555',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              minWidth: '32px',
                            }}
                            onClick={() => handleOpenInlineShipmentForm(pkg, idx)}
                          >
                            {pkg.shipment?.shipment_status === 'DELIVERED' ? (
                              <EyeViewIcon color='#6c757d' />
                            ) : pkg.shipment?.shipment_status === 'SHIPPED' ? (
                              <DeliveryCheckIcon color='#28a745' />
                            ) : (
                              <ShipTruckIcon color='#555' />
                            )}
                          </Button>
                        </OverlayTrigger>
                      </div>
                    </div>

                    {/* Card Body */}
                    <div
                      className='p-3'
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '1.5fr 2fr',
                        gap: '20px',
                        fontSize: '14px',
                      }}
                    >
                      <div>
                        <p className='mb-2'>
                          <strong>Dims:</strong> {dimsString}
                        </p>
                        <p className='mb-0'>
                          <strong>Weight:</strong> {weightString}
                        </p>
                      </div>
                      <div>
                        <p className='fw-bold mb-1'>Items:</p>
                        {renderPackageItems(pkg)}
                      </div>
                    </div>

                    {/* Inline Fulfillment Form */}
                    {expandedFulfillmentPkgId === pkg.id && (
                      <div className="mb-4 shadow-sm mx-3" style={{ transition: 'all 0.3s ease' }}>
                        <ShipmentFulfillmentForm
                          onCancel={() => setExpandedFulfillmentPkgId(null)}
                          orderId={orderId}
                          orderCode={orderCode}
                          pkg={pkg}
                          packageIndex={idx}
                          shipmentData={null}
                          orderLinesList={orderLinesList}
                          onSuccess={() => {
                            setExpandedFulfillmentPkgId(null)
                            handleShipmentSuccess()
                          }}
                          isDirect={false}
                          mode={activeFulfillmentMode}
                        />
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </>
    )
  }

  /** Tab 2 - Package Presets */
  const renderPackagePresetsTab = () => (
    <>
      {/* Presets table */}
      <div className='mb-5'>
        <h6 className='fw-bold mb-3'>Saved Package Presets</h6>
        {presetsLoading ? (
          <div className='d-flex justify-content-center py-4'>
            <Spinner animation='border' size='sm' />
          </div>
        ) : (
          <div className='table-responsive border rounded'>
            <table className='table table-sm align-middle mb-0'>
              <thead className='bg-light'>
                <tr>
                  <th>Name</th>
                  <th>Dimensions (L x W x H)</th>
                  <th>Weight</th>
                  <th>Unit</th>
                  <th className='text-end'>Actions</th>
                </tr>
              </thead>
              <tbody>
                {packageList.length ? (
                  packageList.map((preset: any) => {
                    const dims = extractDimensionsFromPreset(preset)
                    return (
                      <tr
                        key={preset.id}
                        className={presetEditId === preset.id ? 'table-active' : ''}
                      >
                        <td className='fw-semibold'>{preset.api_name || 'N/A'}</td>
                        <td>
                          {dims.packageLength} x {dims.packageWidth} x {dims.packageHeight}{' '}
                          {dims.dimensionUnit}
                        </td>
                        <td>
                          {dims.packageWeight} {dims.weightUnit}
                        </td>
                        <td>{dims.dimensionUnit}</td>
                        <td className='text-end'>
                          <Button
                            variant='outline-primary'
                            size='sm'
                            className='me-2'
                            onClick={() => handleEditPreset(preset)}
                          >
                            Edit
                          </Button>
                          <Button
                            variant='outline-danger'
                            size='sm'
                            onClick={() => handleDeletePreset(preset.id)}
                            disabled={presetMutating}
                          >
                            Delete
                          </Button>
                        </td>
                      </tr>
                    )
                  })
                ) : (
                  <tr>
                    <td colSpan={5} className='text-center text-muted py-6'>
                      No package presets found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create / Edit form */}
      <div className='border rounded p-4' style={{backgroundColor: '#fafbfc'}}>
        <h6 className='fw-bold mb-3'>
          {presetEditId ? 'Edit Package Preset' : 'Create New Package Preset'}
        </h6>

        {/* Dimension fields inline for presets */}
        <div className='d-flex gap-4 mb-3' style={{flexWrap: 'wrap'}}>
          <div style={{flex: '1 1 50%', minWidth: '280px'}}>
            <label className='form-label fw-bold mb-1' style={{fontSize: '13px', color: '#333'}}>
              Dimensions (L x W x H)
            </label>
            <div className='d-flex align-items-center gap-2'>
              <Form.Control type='number' min='0' step='0.01' placeholder='L' value={packageLength} onChange={(e) => setPackageLength(e.target.value)} style={{fontSize: '14px'}} />
              <span className='fw-bold text-muted'>x</span>
              <Form.Control type='number' min='0' step='0.01' placeholder='W' value={packageWidth} onChange={(e) => setPackageWidth(e.target.value)} style={{fontSize: '14px'}} />
              <span className='fw-bold text-muted'>x</span>
              <Form.Control type='number' min='0' step='0.01' placeholder='H' value={packageHeight} onChange={(e) => setPackageHeight(e.target.value)} style={{fontSize: '14px'}} />
              <Form.Select value={dimensionUnit} style={{maxWidth: '75px', fontSize: '14px'}} onChange={(e) => setDimensionUnit(e.target.value)}>
                {DIMENSION_UNITS.map((u) => (<option key={u.value} value={u.value}>{u.label}</option>))}
              </Form.Select>
            </div>
          </div>
          <div style={{flex: '0 1 25%', minWidth: '160px'}}>
            <label className='form-label fw-bold mb-1' style={{fontSize: '13px', color: '#333'}}>Weight</label>
            <div className='d-flex align-items-center gap-2'>
              <Form.Control type='number' min='0' step='0.01' placeholder='0.00' value={packageWeight} onChange={(e) => setPackageWeight(e.target.value)} style={{fontSize: '14px'}} />
              <Form.Select value={weightUnit} style={{maxWidth: '75px', fontSize: '14px'}} onChange={(e) => setWeightUnit(e.target.value)}>
                {WEIGHT_UNITS.map((u) => (<option key={u.value} value={u.value}>{u.label}</option>))}
              </Form.Select>
            </div>
          </div>
          <div style={{flex: '0 1 20%', minWidth: '140px'}}>
            <label className='form-label fw-bold mb-1' style={{fontSize: '13px', color: '#333'}}>Name</label>
            <Form.Control type='text' placeholder='Package name' value={packageName} onChange={(e) => setPackageName(e.target.value)} style={{fontSize: '14px'}} />
          </div>
        </div>

        <div className='d-flex justify-content-end gap-2 mt-4'>
          {presetEditId && (
            <Button variant='secondary' onClick={handleCancelPresetEdit} disabled={presetMutating}>
              Cancel
            </Button>
          )}
          <Button variant='primary' onClick={handleSavePreset} disabled={presetMutating}>
            {presetMutating ? (
              <>
                <Spinner animation='border' size='sm' className='me-2' />
                Saving...
              </>
            ) : presetEditId ? (
              'Update Package'
            ) : (
              'Create Package'
            )}
          </Button>
        </div>
      </div>
    </>
  )

  // ---------------------------------------------------------------
  // Render
  return (
    <>
      <style>{`
        .modal-half-width {
          max-width: 50vw !important;
          width: 50vw !important;
        }
        .professional-modal input::placeholder,
        .professional-modal textarea::placeholder {
          color: #adb5bd !important;
          opacity: 0.6 !important;
        }
        .route-switch-link {
          color: #0d6efd;
          cursor: pointer;
          text-decoration: underline;
          transition: color 0.2s ease;
        }
        .route-switch-link:hover {
          color: #0a58ca;
        }
      `}</style>
      <Modal show={show} onHide={onClose} centered dialogClassName='professional-modal modal-half-width' backdrop='static' keyboard={!saving} backdropClassName='blur-backdrop'>
        <Modal.Header closeButton={!saving}>
          <div>
            <Modal.Title style={{fontSize: '20px', fontWeight: 700}}>Manage Packages</Modal.Title>
            <Badge bg='light' text='secondary' className='border border-secondary px-3 py-2 rounded-pill' style={{ fontSize: '12px', fontWeight: 500 }}>
              Order {orderCode ? `#${orderCode}` : ''}
            </Badge>
          </div>
        </Modal.Header>

        <Modal.Body>
          {/* Tab navigation - hidden per user request to save space */}
          <Nav variant='pills' className='mb-4' style={{ display: 'none' }}>
            <Nav.Item>
              <Nav.Link
                active={activeTab === 'order-packages'}
                onClick={() => setActiveTab('order-packages')}
                style={{cursor: 'pointer', fontSize: '14px'}}
              >
                {'📦'} Order Packages
              </Nav.Link>
            </Nav.Item>
            {/* Package Presets tab - hidden for now, code preserved for future use */}
            <Nav.Item style={{display: 'none'}}>
              <Nav.Link
                active={activeTab === 'package-presets'}
                onClick={() => setActiveTab('package-presets')}
                style={{cursor: 'pointer', fontSize: '14px'}}
              >
                {'⚙️'} Package Presets
              </Nav.Link>
            </Nav.Item>
          </Nav>

          {initialLoading ? (
            <div className='d-flex justify-content-center align-items-center py-5'>
              <Spinner animation='border' size='sm' className='me-2' />
              <span className='text-muted'>Loading package data...</span>
            </div>
          ) : (
            <>
              {/* Tab content */}
              {activeTab === 'order-packages' && renderOrderPackagesTab()}
              {activeTab === 'package-presets' && renderPackagePresetsTab()}
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button 
            variant='light' 
            onClick={onClose} 
            disabled={saving}
            style={{ padding: '10px 28px', fontWeight: 600, color: '#475569', border: '1px solid #cbd5e1', borderRadius: '8px' }}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>


      <CommonConfirmationModal
        show={deletePkgModalState.show}
        title="Delete Package"
        message="Are you sure you want to delete this package?"
        confirmText="Delete"
        confirmVariant="danger"
        onConfirm={proceedDeleteOrderPackage}
        onCancel={() => setDeletePkgModalState({ show: false, id: null })}
      />
    </>
  )
}

export default ManagePackagesModal

